<template>
  <div v-if="orderInitialLoading" class="table-group">
    <div class="table-loader"></div>
  </div>

  <div v-if="orderError" class="mc-error">{{ orderError }}</div>

  <template v-if="orderLoaded && order && order.id">
    <div class="header-wrapper header-wrapper--row shipment-page-header">
      <div class="header-group flex-column">
        <div class="flex">
          <div class="h1">{{ t('orderPage.title', { number: order.number }) }}</div>
          <div class="label border-orange">
            <span class="font-14">{{ statusLabel(order.status) }}</span>
          </div>
        </div>
        <div class="flex txt-red order-notice" v-if="order.has_refusal_positions">
          <svg class="svg-note">
            <use href="/img/sprite.svg#svg-note"></use>
          </svg>
          <span class="font-14">{{ t('orderPage.hasRefusalPositions') }}</span>
        </div>
      </div>
      <div class="buttons-row">
        <router-link
          v-if="order.permissions?.can_send_message"
          class="btn"
          :to="{name: 'order-message', params: {id: order.id}, query: $route.query}"
        >{{ t('orderMessages.send') }}</router-link>
        <button v-if="canImportShipment" class="btn" type="button" @click="openShipmentImport">{{ t('shipmentImportPages.upload') }}</button>
        <a class="btn" @click="showDates = true"> {{ t('orderPage.importExcel') }}</a>
        <a class="btn" :class="{loader:processExport}" @click="exportExcel"> {{ t('orderPage.exportExcel') }}</a>
      </div>
    </div>
    <ul class="info-list">
      <li class="info-list-item">
        <p class="muted-14">{{ t('orderPage.supplier') }}</p>
        <p class="font-16">{{ order.partner_name }}</p>
      </li>
      <li class="info-list-item">
        <p class="muted-14">{{ t('orderPage.filial') }}</p>
        <p class="font-16">{{ displayEntityText(order.filial_name, order.filial_name_en) }}</p>
      </li>
      <li class="info-list-item">
        <p class="muted-14">{{ t('orderPage.orderDate') }}</p>
        <p class="font-16">{{order.date}}</p>
      </li>
      <li class="info-list-item">
        <p class="muted-14">{{ t('orderPage.orderNumber') }}</p>
        <p class="font-16">{{ order.number }}</p>
      </li>
      <li class="info-list-item">
        <p class="muted-14">{{ t('orderPage.orderSupplierNumber') }}</p>
        <p class="font-16">{{ order.supplier_number }}</p>
      </li>
      <li class="info-list-item">
        <p class="muted-14">{{ t('orderPage.invoisNumber') }}</p>
        <p class="font-16">{{ order.invois }}</p>
      </li>
      <li class="info-list-item">
        <p class="muted-14">{{ t('orderPage.invoisDate') }}</p>
        <p class="font-16">{{ order.invois_date }}</p>
      </li>
    </ul>

    <div v-if="shipmentNotice" class="download-zone-note" role="status">
      <p>{{ shipmentNotice }}</p>
      <button class="btn-outline" type="button" :disabled="shipmentRefreshing" @click="refreshShipmentTable()">{{ t('shipmentImportPages.refresh') }}</button>
    </div>
    <ShipmentImportDialog
      :is-open="showShipmentImport"
      :scope="shipmentScope"
      :report-detached-cleanup-error="onShipmentCleanupError"
      @close="showShipmentImport = false"
      @completed="onShipmentCompleted"
      @cleanup-error="onShipmentCleanupError"
    />

    <Positions
        ref="positionsTable"
        mode="order"
        :order="order"
        :positions="positions"
        :pagination="positionsPagination"
        :server-sort="positionsSort"
        :filter-options="filterOptions"
        :initial-filters="currentFilters()"
        :loading="orderLoading"
        :orderLoaded="orderLoaded"
        @change-page="changePage"
        @change-sort="changeSort"
        @change-filters="changeFilters"
        @show-message-history="showMessageHistory = true"
    ></Positions>

    <div class="buttons-grid four-columns" v-if="user.role == 'manager' || user.role == 'admin'">
      <a @click="sendPartner" v-if="isDraftOrder(order.status)" class="btn w-100">
        {{ t('orderPage.sendPartner') }}
      </a>
      <a @click="openFinishConfirm" v-if="!isFinishedOrder(order.status)" class="btn w-100" :class="{loader:isSubmitting=='finishOrder'}">
        {{ t('orderPage.finish') }}
      </a>
      <a v-if="false" class="btn-outline w-100">{{ t('orderPage.saveAsDraft') }}</a>
      <a v-if="false" class="btn-outline w-100">{{ t('orderPage.markAsReceived') }}</a>
      <a v-if="false" class="btn-outline w-100">{{ t('orderPage.markAsRefusal') }}</a>
    </div>
    <div class="attachment-block bg-biruza">
      <div v-if="order.files.length > 0" class="attachment-list">
        <div v-for="file in order.files" :key="file.id" class="attachment-item flex">
          <button class="attachment-file-link flex" type="button" @click="downloadFile(file)">
            <svg class="svg-attach">
              <use href="/img/sprite.svg#svg-attach"></use>
            </svg>
            <p>{{ file.name }}</p>
          </button>
          <button @click.stop="removeFile(file.id)" class="btn-remove">
            <svg class="svg-cross">
              <use href="/img/sprite.svg#svg-cross"></use>
            </svg>
          </button>
        </div>
      </div>
      <div class="attachment-button">

          <div class="input-file" @click="showFilesLoad=true">

            <span class="btn-outline btn-slim">{{ t('orderPage.addFiles') }}</span>
          </div>

      </div>
    </div>
    <div class="inner-box" v-if="user.role == 'supplier' && isSentOrder(order.status)">
      <button @click="acceptInWork" class="btn" :class="{loader: isSubmitting=='acceptInWork'}" :disabled="isSubmitting === 'acceptInWork'" type="button">{{ t('orderPage.acceptInWork') }}</button>
    </div>

    <Dates :isOpen="showDates" :id="order.id" @close="showDates = false" @imported="handlePositionsImported" />
    <FilesLoad :isOpen="showFilesLoad" :id="order.id" @close="showFilesLoad = false" />
    <OrderMessagesHistory
      v-if="showMessageHistory"
      :is-open="showMessageHistory"
      :order-id="order.id"
      @close="showMessageHistory = false"
    />

    <div class="modal-frame" :class="{active: showFinishConfirm}">
      <div class="modal-frame-content">
        <button class="btn-close" rel="button_close" type="button" @click="closeFinishConfirm">
          <svg class="svg-cross">
            <use href="/img/sprite.svg#svg-cross"></use>
          </svg>
        </button>
        <div class="modal-frame-header">
          <div class="h3">{{ t('modals.confirmFinish') }}</div>
        </div>
        <div class="modal-footer font-18">
          <p class="txt-center">{{ t('modals.finishOrderQuestion') }}</p>
        </div>
        <div class="flex-center">
          <button class="btn" type="button" :class="{loader:isSubmitting === 'finishOrder'}" @click="finishOrder">
            {{ t('modals.yes') }}
          </button>
          <button class="btn-outline" type="button" @click="closeFinishConfirm">{{ t('modals.no') }}</button>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import {mapActions, mapGetters, mapMutations} from "vuex";

import Dates from '@/components/order/Dates.vue';
import FilesLoad from '@/components/order/FilesLoad.vue';
import Positions from '@/components/order/Positions.vue';
import OrderMessagesHistory from '@/components/order/OrderMessagesHistory.vue';

import { useI18n } from 'vue-i18n';
import axios from "axios";

import importexport from '@/mixin/importexport.js';
import shipmentImportPage from '@/mixin/shipmentImportPage';
import { isOrderStatus, ORDER_STATUS_VALUES, translateOrderStatus } from '@/utils/statusUtils';
import { translateApiError } from '@/utils/apiErrorUtils';
import { localizedDisplayValue, localizedEntityValue } from '@/utils/transliteration';

export default {
  components: {
    Positions,
    Dates,
    FilesLoad,
    OrderMessagesHistory,
  },
  mixins: [importexport, shipmentImportPage],
  data: () => ({
    showFilesLoad: false,
    showDates: false,
    showFinishConfirm: false,
    showMessageHistory: false,
    isSubmitting: null,
    serverError: null,
    successMessage: null,

  }),
  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },
  created() {
    this.initializeFilters();
  },
  watch: {
    '$route.query'() {
      this.initializeFilters();
    },
    '$route.params.id'() {
      this.initializeFilters();
    },
  },
  computed: {
    ...mapGetters('order', [
      'order',
      'positionsPagination',
      'positionsSort',
      'filterOptions',
      'orderLoading',
      'orderError',
      'orderLoaded',
    ]),
    ...mapGetters('positions', [
      'positions'
    ]),
    ...mapGetters([
      'user'
    ]),
    canImportShipment() {
      return !this.orderError && String(this.order.id) === String(this.$route.params.id) && this.order.permissions?.can_import_shipment === true;
    },
    orderInitialLoading() {
      return this.orderLoading && !this.orderLoaded;
    },
  },
  methods: {
    ...mapActions('order', [
      'fetchOrder',
        'setFile'
    ]),
    ...mapActions('notify', [
      'addNotify',
    ]),
    async initializeFilters() {
      const query = this.withoutLegacyDateFilters(this.$route.query);
      if (Object.keys(query).length !== Object.keys(this.$route.query).length) {
        await this.$router.replace({name: this.$route.name, params: this.$route.params, query});
        return;
      }
      await this.load();
    },
    withoutLegacyDateFilters(query) {
      return Object.entries(query).reduce((result, [key, value]) => {
        if (!key.startsWith('f_date_')) result[key] = value;
        return result;
      }, {});
    },
    async load() {
      const data = await this.fetchOrder({
        id: this.$route.params.id,
        page: this.currentPage(),
        sort: this.currentSort(),
        sortDir: this.currentSortDir(),
        filters: this.currentFilterParams(),
      });

      if (!data) return null;
      if (!this.order || !this.order.id) {
        this.$router.push({name: 'main'});
      }

      if (this.order.view)
        this.viewOrder();
      return data;
    },
    statusLabel(status) {
      return translateOrderStatus(status, this.t, this.isSupplier());
    },
    isDraftOrder(status) {
      return isOrderStatus(status, 'new');
    },
    isSentOrder(status) {
      return isOrderStatus(status, 'send');
    },
    isFinishedOrder(status) {
      return isOrderStatus(status, 'finish');
    },
    displayText(value) {
      return localizedDisplayValue(value, this.isSupplier() ? 'en' : this.locale);
    },
    displayEntityText(value, valueEn) {
      return localizedEntityValue(value, valueEn, this.isSupplier() ? 'en' : this.locale);
    },
    isSupplier() {
      return this.user?.role === 'supplier';
    },
    openFinishConfirm() {
      this.showFinishConfirm = true;
    },
    closeFinishConfirm() {
      if (this.isSubmitting === 'finishOrder') {
        return;
      }

      this.showFinishConfirm = false;
    },
    currentPage() {
      return parseInt(this.$route.query.page, 10) || 1;
    },
    changePage(page) {
      if (page === this.currentPage()) {
        return;
      }

      this.$router.push({
        name: this.$route.name,
        params: this.$route.params,
        query: {
          ...this.$route.query,
          page,
        },
      });
    },
    currentSort() {
      return this.$route.query.sort || '';
    },
    currentSortDir() {
      return this.$route.query.sort_dir === 'desc' ? 'desc' : 'asc';
    },
    changeSort(sort) {
      this.$router.push({
        name: this.$route.name,
        params: this.$route.params,
        query: {
          ...this.$route.query,
          page: 1,
          sort: sort.key || undefined,
          sort_dir: sort.dir || undefined,
        },
      });
    },
    changeFilters(filters) {
      this.$router.push({
        name: this.$route.name,
        params: this.$route.params,
        query: {
          ...this.queryWithoutFilters(),
          page: 1,
          ...this.buildFilterQuery(filters),
        },
      });
    },
    currentFilters() {
      const query = this.$route.query;

      return {
        partner: this.queryArray(query.f_partner),
        filial: this.queryArray(query.f_filial),
        color: this.queryArray(query.f_color),
        storage_delivery: this.queryArray(query.f_storage_delivery),
        marking: this.queryArray(query.f_marking),
        empty_date: this.queryArray(query.f_empty_date),
        filled_date: this.queryArray(query.f_filled_date),
        arrived: query.f_arrived === '1',
        search: query.f_search || '',
      };
    },
    currentFilterParams() {
      return this.buildFilterQuery(this.currentFilters());
    },
    queryArray(value) {
      if (Array.isArray(value)) {
        return value.filter(item => item !== '');
      }
      return value ? [value] : [];
    },
    queryWithoutFilters() {
      return Object.entries(this.$route.query).reduce((result, [key, value]) => {
        if (key !== 'page' && !key.startsWith('f_')) {
          result[key] = value;
        }
        return result;
      }, {});
    },
    buildFilterQuery(filters) {
      const query = {
        f_partner: filters.partner?.length ? filters.partner : undefined,
        f_filial: filters.filial?.length ? filters.filial : undefined,
        f_color: filters.color?.length ? filters.color : undefined,
        f_storage_delivery: filters.storage_delivery?.length ? filters.storage_delivery : undefined,
        f_marking: filters.marking?.length ? filters.marking : undefined,
        f_empty_date: filters.empty_date?.length ? filters.empty_date : undefined,
        f_filled_date: filters.filled_date?.length ? filters.filled_date : undefined,
        f_arrived: filters.arrived ? '1' : undefined,
        f_search: filters.search || undefined,
      };

      return query;
    },
    async removeFile(id) {
      this.isSubmitting = true;
      this.serverError = null;
      this.successMessage = null;

      const formDataToSubmit = {
        file_id: id,
      };

      try {

        let url = `/api/order/file/remove/${this.order.id}/`;

        // отправка данных на сервер
        const response = await axios.post(url, formDataToSubmit,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
        if (response.data.status == 'error') {
          this.serverError= '';
          for (let i in response.data.errors) {
            console.log(typeof response.data.errors[i].code)
            if (typeof response.data.errors[i].code == 'number') {
              this.serverError = this.t('errors.serverError');
              break;
            }
            else
              this.serverError += translateApiError(response.data.errors[i], this.t) + "<br>";
          }
          throw new Error(this.serverError);
        }
        else {
          this.setFile(response.data.data);
          this.fileDocs = [];
          this.fileIds = [];
          this.successMessage = this.t('orderPage.notify.fileDeleted');
          setTimeout(() => {
            this.successMessage = null;
          }, 5000);
        }

      } catch (error) {
        this.serverError = error;
        //this.scrollToFirstError();
      } finally {
        this.isSubmitting = false;
      }
    },
    getFileUrl(file) {
      return file?.src || file?.link || '#!';
    },
    downloadFile(file) {
      const url = this.getFileUrl(file);
      if (url === '#!') {
        return;
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = file?.name || '';
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async sendPartner() {
      this.isSubmitting = 'sendPartner';
      this.serverError = null;
      this.successMessage = null;

      const formDataToSubmit = {};

      try {

        let url = `/api/order/send/${this.order.id}/`;

        // отправка данных на сервер
        const response = await axios.post(url, formDataToSubmit,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
        if (response.data.status == 'error') {
          this.serverError= '';
          for (let i in response.data.errors) {
            console.log(typeof response.data.errors[i].code)
            if (typeof response.data.errors[i].code == 'number') {
              this.serverError = this.t('errors.serverError');
              break;
            }
            else
              this.serverError += translateApiError(response.data.errors[i], this.t) + "<br>";
          }
          throw new Error(this.serverError);
        }
        else {
          this.order.status = ORDER_STATUS_VALUES.send;
          this.addNotify({text: this.t('orderPage.notify.sentToPartner')});
        }

      } catch (error) {
        this.serverError = error;
        //this.scrollToFirstError();
      } finally {
        this.isSubmitting = false;
      }
    },
    async acceptInWork() {
      if (this.isSubmitting === 'acceptInWork') {
        return;
      }

      this.isSubmitting = 'acceptInWork';
      this.serverError = null;
      this.successMessage = null;

      const formDataToSubmit = {};

      try {

        let url = `/api/order/work/${this.order.id}/`;

        // отправка данных на сервер
        const response = await axios.post(url, formDataToSubmit,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
        if (response.data.status == 'error') {
          this.serverError= '';
          for (let i in response.data.errors) {
            console.log(typeof response.data.errors[i].code)
            if (typeof response.data.errors[i].code == 'number') {
              this.serverError = this.t('errors.serverError');
              break;
            }
            else
              this.serverError += translateApiError(response.data.errors[i], this.t) + "<br>";
          }
          throw new Error(this.serverError);
        }
        else {
          await this.load();
          this.addNotify({text: this.t('orderPage.notify.acceptedInWork')});
        }

      } catch (error) {
        this.serverError = error;
        //this.scrollToFirstError();
      } finally {
        this.isSubmitting = false;
      }
    },
    async finishOrder() {
      this.isSubmitting = 'finishOrder';
      this.serverError = null;
      this.successMessage = null;

      const formDataToSubmit = {};

      try {

        let url = `/api/order/finish/${this.order.id}/`;

        // РѕС‚РїСЂР°РІРєР° РґР°РЅРЅС‹С… РЅР° СЃРµСЂРІРµСЂ
        const response = await axios.post(url, formDataToSubmit,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
        if (response.data.status == 'error') {
          this.serverError= '';
          for (let i in response.data.errors) {
            console.log(typeof response.data.errors[i].code)
            if (typeof response.data.errors[i].code == 'number') {
              this.serverError = this.t('errors.serverError');
              break;
            }
            else
              this.serverError += translateApiError(response.data.errors[i], this.t) + "<br>";
          }
          throw new Error(this.serverError);
        }
        else {
          this.order.status = ORDER_STATUS_VALUES.finish;
          this.addNotify({text: this.t('orderPage.notify.finished')});
          this.showFinishConfirm = false;
        }

      } catch (error) {
        this.serverError = error;
        //this.scrollToFirstError();
      } finally {
        this.isSubmitting = false;
      }
    },
    async viewOrder() {
      this.isSubmitting = true;
      this.serverError = null;
      this.successMessage = null;

      const formDataToSubmit = {};

      try {

        let url = `/api/order/open/${this.order.id}/`;

        // отправка данных на сервер
        const response = await axios.post(url, formDataToSubmit,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
        if (response.data.status == 'error') {
          this.serverError= '';
          for (let i in response.data.errors) {
            console.log(typeof response.data.errors[i].code)
            if (typeof response.data.errors[i].code == 'number') {
              this.serverError = this.t('errors.serverError');
              break;
            }
            else
              this.serverError += translateApiError(response.data.errors[i], this.t) + "<br>";
          }
          throw new Error(this.serverError);
        }
        else {
          this.order.view = false;
        }

      } catch (error) {
        this.serverError = error;
        //this.scrollToFirstError();
      } finally {
        this.isSubmitting = false;
      }
    },

  }
}
</script>

<style scoped>
.attachment-file-link {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: none;
}
</style>
